/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, createRef } from 'react';

import axios from 'axios';
import * as download from 'downloadjs';
import { Location } from 'history';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import queryString from 'query-string';
import BlockUi from 'react-block-ui';
import * as intl from 'react-intl-universal';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  UncontrolledButtonDropdown,
} from 'reactstrap';

import ApiError from 'api/common/types/ApiError';
import ProjectsApiInstance from 'api/projects/ProjectsApi';
import SettingsApiInstance from 'api/settings/SettingsApi';
import UpdateFacilitatorsByBulk from 'api/settings/types/facilitators/UpdateFacilitatorsByBulk';
import ActionKeysGA from 'constants/ga/ActionKeysGA';
import CategoryKeysGA from 'constants/ga/CategoryKeysGA';
import ModulePaths from 'constants/ModulePaths';
import ResourceKeys from 'constants/permissions/ResourceKeys';
import Tables from 'constants/Tables';
import { formatDate } from 'helpers/DateFormat';
import { sendEventGA } from 'helpers/GoogleAnalyticsHelper';
import PermissionUtil from 'helpers/PermissionUtil';
import setPageTitle from 'helpers/setPageTitle';
import ProjectFacilitatorsDataTable from 'modules/private/projects/components/project-facilitators-data-table/ProjectFacilitatorsDataTable';
import EllipsisTooltip from 'shared/components/ellipsis-tooltip/EllipsisTooltip';
import { Option as OptionType } from 'shared/components/ins-form-fields/formik-select/FormikSelectOption';
import InsLink from 'shared/components/ins-link/InsLink';
import ScrollToTopOnMount from 'shared/components/scroll-to-top-on-mount/ScrollToTopOnMount';
import DateFormatType from 'shared/enums/DateFormatType';
import HTTP_STATUS from 'shared/enums/HttpStatus';
import Status from 'shared/enums/Status';

import ProjectFacilitatorViewModel, {
  Supervisor as ProjectSupervisor,
} from '../projects-view/ProjectFacilitatorViewModel';
import SupervisorsViewModel from '../projects-view/SupervisorsViewModel';
import ProjectFacilitatorsViewProps from './ProjectFacilitatorsViewProps';
import ProjectFacilitatorsViewState, {
  ProjectFacilitatorsSearchValues,
  SelectedRows,
} from './ProjectFacilitatorsViewState';
import TableConfig from './TableConfig';

class ProjectFacilitatorsView extends Component<
  ProjectFacilitatorsViewProps,
  ProjectFacilitatorsViewState
> {
  constructor(props: ProjectFacilitatorsViewProps) {
    super(props);

    const { appContext } = props;
    const { permissionsData } = appContext;
    const canAddFacilitators = PermissionUtil.Can(
      permissionsData.claims,
      ResourceKeys.ProjectsItemAddFacilitators
    );
    const canEditFacilitators = PermissionUtil.Can(
      permissionsData.claims,
      ResourceKeys.ProjectsFacilitatorsEdit
    );
    const canDownloadCSV = PermissionUtil.Can(
      permissionsData.claims,
      ResourceKeys.ExportCSV
    );
    const canDownloadPDF = PermissionUtil.Can(
      permissionsData.claims,
      ResourceKeys.ExportPDF
    );

    this.saveButtonRef = createRef();
    this.okayButtonRef = createRef();

    this.state = {
      data: [],
      editModeEnabled: false,
      exportDisabled: false,
      canAddFacilitators,
      canEditFacilitators,
      canDownloadCSV,
      canDownloadPDF,
      error: null,
      status: Status.Idle,
      projectData: {
        code: '',
        name: '',
        status: Status.Idle,
      },
      pageData: {
        pageCount: 1,
        pagination: { total: 0, page: 1 },
        selectedRows: {},
      },
      edit: {
        updatedData: {},
        showRemoveFacilitatorsModal: false,
        editStatus: Status.Idle,
        supervisors: [],
        supervisorsFiltered: [],
        supervisorsStatus: Status.Idle,
      },
    };
  }

  componentDidMount(): void {
    const { match, location, appContext } = this.props;
    appContext.hideErrorToast();
    setPageTitle(intl.get('LBL_PROJECT_FACILITATORS_TITLE'));

    const { page, size, sortBy } = this.getTablePreferencesFromSearch(location);

    const projectId = match?.params.projectId ?? '';
    this.loadSelectedProjectData(projectId);
    this.loadProjectFacilitatorsListData(projectId, { size, page, sortBy });
    this.fetchSupervisors();
  }

  componentDidUpdate(
    prevProps: ProjectFacilitatorsViewProps,
    prevState: ProjectFacilitatorsViewState
  ): void {
    const { match, location } = this.props;
    const { editModeEnabled } = this.state;

    const projectId = match?.params.projectId ?? '';

    if (location.search !== prevProps.location.search) {
      const prevSearch = this.getTablePreferencesFromSearch(prevProps.location);
      const currentSearch = this.getTablePreferencesFromSearch(location);
      const { page, size, sortBy } = currentSearch;
      if (
        prevSearch.size !== size ||
        prevSearch.page !== page ||
        !isEqual(prevSearch.sortBy, sortBy)
      ) {
        this.loadProjectFacilitatorsListData(projectId, { page, size, sortBy });
        this.fetchSupervisors();
      }
    }
    if (prevState.editModeEnabled !== editModeEnabled) {
      const currentSearch = this.getTablePreferencesFromSearch(location);
      const { page, size, sortBy } = currentSearch;
      if (editModeEnabled) {
        this.fetchSupervisors();
      } else {
        this.loadProjectFacilitatorsListData(projectId, { page, size, sortBy });
        this.setEditStateAttribute({
          updatedData: {},
          editStatus: Status.Idle,
        });
      }
    }
  }

  componentWillUnmount(): void {
    this.source.cancel();
  }

  CancelToken = axios.CancelToken;

  source = this.CancelToken.source();

  saveButtonRef: React.RefObject<HTMLButtonElement>;

  okayButtonRef: React.RefObject<HTMLButtonElement>;

  /**
   * Set/Update component state in the root slice
   *
   * @param updateState Updated state as an object
   */
  setStateAttribute = (
    updateState: Partial<ProjectFacilitatorsViewState>
  ): void =>
    this.setState((state) => ({
      ...state,
      ...updateState,
    }));

  /**
   * Set/Update component state in the projectData slice
   *
   * @param updateState Updated state as an object
   */
  setProjectDataStateAttribute = (
    updateState: Partial<ProjectFacilitatorsViewState['projectData']>
  ): void =>
    this.setState((state) => ({
      ...state,
      projectData: { ...state.projectData, ...updateState },
    }));

  /**
   * Set/Update component state in the pageData slice
   *
   * @param updateState Updated state as an object
   */
  setPageDataStateAttribute = (
    updateState: Partial<ProjectFacilitatorsViewState['pageData']>
  ): void =>
    this.setState((state) => ({
      ...state,
      pageData: { ...state.pageData, ...updateState },
    }));

  /**
   * Set/Update component state in the edit slice
   *
   * @param updateState Updated state as an object
   * @param callback An optional callback to fire after the updated state is set
   */
  setEditStateAttribute = (
    updateState: Partial<ProjectFacilitatorsViewState['edit']>,
    callback?
  ): void =>
    this.setState(
      (state) => ({
        ...state,
        edit: { ...state.edit, ...updateState },
      }),
      callback
    );

  /**
   * Extract and return table preferences from location.search strinG
   *
   * @param location History.Location from react-router props
   * @returns {ProjectFacilitatorsSearchValues} Table preferences object
   */
  getTablePreferencesFromSearch = (
    location: Location
  ): ProjectFacilitatorsSearchValues => {
    let { size, page, by, desc } = queryString.parse(location.search);

    size = size && Array.isArray(size) ? null : size;
    page = page && Array.isArray(page) ? null : page;
    by = by && Array.isArray(by) ? null : by;
    desc = desc && Array.isArray(desc) ? null : desc;

    const sizeNumber = size ? parseInt(size, 10) : -1;

    const newSize = Tables.PageSizes.includes(sizeNumber)
      ? sizeNumber
      : Tables.PageSizes[0];

    const descending = desc ? desc !== 'false' : false;

    const sortColumn =
      by && TableConfig.sortCols.has(by) ? by : TableConfig.defaultSort;

    const sortBy = [{ id: sortColumn, desc: descending }];

    const pageNumber = page ? parseInt(page, 10) : -1;

    const newPage =
      pageNumber !== undefined &&
      pageNumber !== null &&
      !Number.isNaN(pageNumber) &&
      pageNumber >= 0
        ? pageNumber
        : 0;

    return { size: newSize, sortBy, page: newPage };
  };

  /**
   * Fetch project data and set to component state
   *
   * @param projectId Project ID
   */
  loadSelectedProjectData = async (projectId: string): Promise<void> => {
    try {
      this.setProjectDataStateAttribute({ status: Status.Loading });
      const response = await ProjectsApiInstance.GetProject(
        projectId,
        this.source
      );
      if (response.item) {
        const result = response.item;
        this.setProjectDataStateAttribute({
          code: result.code,
          name: result.name,
          status: Status.Success,
        });
      }
    } catch (error) {
      this.setProjectDataStateAttribute({ status: Status.Error });
    }
  };

  /**
   * Fetch project facilitators list data and set to component state
   *
   * @param projectId Project ID
   * @param tableQuery Table preferences { pageSize, pageIndex, sortBy}
   * @param resetPageIndex if true will reset the page index to 0
   */
  loadProjectFacilitatorsListData = async (
    projectId: string,
    tableQuery: ProjectFacilitatorsSearchValues,
    resetPageIndex?: boolean
  ): Promise<void> => {
    const { appContext } = this.props;
    const { size, page, sortBy } = tableQuery;
    const Direction = Tables.SortDirection;

    this.setStateAttribute({ status: Status.Loading });

    try {
      const sortByAttribute = sortBy[0].id;
      // prettier-ignore
      const sortDirection = sortBy[0].desc ? Direction.Descending : Direction.Ascending;
      const pageIndexAltered = resetPageIndex ? 0 : page;

      const result = await ProjectsApiInstance.GetProjectFacilitators(
        projectId,
        this.source,
        {
          pageSize: size,
          page: pageIndexAltered,
          sortBy: sortByAttribute,
          sort: sortDirection,
        }
      );

      const pageCount = Math.ceil(result.pagination.total / size);

      this.setStateAttribute({
        data: result.items,
        exportDisabled: isEmpty(result.items),
        status: Status.Success,
      });
      this.setPageDataStateAttribute({
        pagination: result.pagination,
        pageCount,
      });
    } catch (error) {
      if (error instanceof ApiError) {
        this.setStateAttribute({
          data: [],
          error,
          exportDisabled: true,
          status: Status.Error,
        });
        if (error.status !== HTTP_STATUS.FORBIDDEN) {
          appContext.setErrorToastText(
            intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
          );
        }
      } else {
        this.setStateAttribute({
          data: [],
          error: new Error(
            intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
          ),
          exportDisabled: true,
          status: Status.Error,
        });
        appContext.setErrorToastText(
          intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
        );
      }
      this.setPageDataStateAttribute({
        pagination: { total: 0, page: 1 },
        pageCount: 0,
      });
    }
  };

  /**
   * Fetch supervisors for search query
   *
   * @param search Search string query
   */
  fetchSupervisors = async (search?: string): Promise<void> => {
    this.setEditStateAttribute({ supervisorsStatus: Status.Loading });
    const noneOption = new SupervisorsViewModel();
    let supervisors: OptionType[] = [
      { label: noneOption.name, value: noneOption.id },
    ];

    try {
      const orgFacilitatorsResponse =
        await SettingsApiInstance.LookupOrganizationUsers(this.source, search);

      const supervisorsFormatted = orgFacilitatorsResponse.items.map(
        ({ userId, name }) => ({
          label: name,
          value: userId,
        })
      );

      supervisors = [...supervisors, ...supervisorsFormatted];

      if (search === undefined) {
        this.setEditStateAttribute({
          supervisors,
          supervisorsFiltered: supervisors,
          supervisorsStatus: Status.Success,
        });
      } else {
        this.setEditStateAttribute({
          supervisorsFiltered: supervisors,
          supervisorsStatus: Status.Success,
        });
      }
    } catch (error) {
      this.setEditStateAttribute({
        supervisors: [],
        supervisorsFiltered: [],
        supervisorsStatus: Status.Error,
      });
    }
  };

  /**
   * Push sortBy update to url; additionally sets page to 0
   *
   * @param sortBy sortBy object of shape { id: string, desc: boolean }
   */
  updateSortBy = (sortBy: ProjectFacilitatorsSearchValues['sortBy']): void => {
    const { history, match, location } = this.props;
    const projectId = match?.params.projectId ?? '';
    const searchParsed = queryString.parse(location.search);

    const { id: by, desc } = sortBy[0];

    if (by !== searchParsed.by || desc.toString() !== searchParsed.desc) {
      // prettier-ignore
      const query = {...searchParsed, by , desc, page: 0 };
      const url = queryString.stringifyUrl({
        url: `${ModulePaths.ProjectsPath}/${projectId}${ModulePaths.ProjectFacilitatorsPath}`,
        query,
      });
      history.push(url);
    }
  };

  /**
   * Push size update to url
   *
   * @param size Current page size
   */
  updatePageSize = (size: number): void => {
    const { history, match, location } = this.props;
    const projectId = match?.params.projectId ?? '';

    const searchParsed = queryString.parse(location.search);

    if (size.toString() !== searchParsed.size) {
      const query = { ...searchParsed, size, page: 0 };
      const url = queryString.stringifyUrl({
        url: `${ModulePaths.ProjectsPath}/${projectId}${ModulePaths.ProjectFacilitatorsPath}`,
        query,
      });
      history.push(url);
    }
  };

  /**
   * Push page update to url
   *
   * @param page Current page
   */
  updatePage = (page: number): void => {
    const { history, match, location } = this.props;
    const projectId = match?.params.projectId ?? '';

    const searchParsed = queryString.parse(location.search);

    if (page.toString() !== searchParsed.page) {
      const query = { ...searchParsed, page };
      const url = queryString.stringifyUrl({
        url: `${ModulePaths.ProjectsPath}/${projectId}${ModulePaths.ProjectFacilitatorsPath}`,
        query,
      });
      history.push(url);
    }
  };

  /**
   * Sets selected rows to component state
   *
   * @param selectedRows Collection of currently selected rows
   */
  setSelectedRows = (selectedRows: SelectedRows): void => {
    this.setPageDataStateAttribute({
      selectedRows,
    });
  };

  /**
   * Toggles edit mode and sets updated data and selected rows to default state
   */
  toggleEditMode = (): void => {
    const { editModeEnabled: editMode } = this.state;
    this.setStateAttribute({
      editModeEnabled: !editMode,
    });
  };

  /**
   * Toggles the remove facilitators modal
   */
  toggleRemoveFacilitatorsModal = (): void => {
    const { edit } = this.state;
    this.setEditStateAttribute({
      showRemoveFacilitatorsModal: !edit.showRemoveFacilitatorsModal,
    });
  };

  /**
   * Handle row click action to navigate to facilitators profile
   */
  handleRowClick = (f): void => f;

  /**
   * Adds an updated facilitator to component state temporarily
   *
   * @param facilitatorId ID of the updated facilitator
   * @param columnId Name of the updated facilitator property
   * @param value Updated property value
   */
  onUpdateFacilitator = (
    facilitatorId: string,
    columnId: string,
    value: ProjectSupervisor
  ): void => {
    const { data, edit } = this.state;
    const idKeys = Object.keys(edit.updatedData);
    if (idKeys.includes(facilitatorId)) {
      let updatedFacilitator: ProjectFacilitatorViewModel;
      if (value.id === 'NONE') {
        /* If the original supervisor selection was 'None' and 
        the user updates to another supervisor and decides to 
        revert back to 'None' (in same edit session), update 
        the supervisor property as null */
        updatedFacilitator = {
          ...edit.updatedData[facilitatorId],
          [columnId]: null,
        };
      } else {
        updatedFacilitator = {
          ...edit.updatedData[facilitatorId],
          [columnId]: value,
        };
      }
      const facilitator = find(data, { id: facilitatorId }) ?? null;
      if (isEqual(facilitator, updatedFacilitator)) {
        const newKeys = idKeys.filter((key) => key !== facilitatorId);
        const newUpdatedData = pick(edit.updatedData, newKeys);
        this.setEditStateAttribute({
          updatedData: newUpdatedData,
        });
      } else {
        this.setEditStateAttribute({
          updatedData: {
            ...edit.updatedData,
            [facilitatorId]: updatedFacilitator,
          },
        });
      }
    } else {
      const facilitator = find(data, { id: facilitatorId }) ?? null;
      if (facilitator) {
        const updatedFacilitator: ProjectFacilitatorViewModel = {
          ...facilitator,
          [columnId]: value,
        };
        this.setEditStateAttribute({
          updatedData: {
            ...edit.updatedData,
            [updatedFacilitator.id]: updatedFacilitator,
          },
        });
      }
    }
  };

  /**
   * Handles downloading the excel file for the project facilitators list
   */
  onExcelDownload = async (): Promise<void> => {
    const { match, appContext } = this.props;
    const projectId = match?.params.projectId ?? '';
    const {
      projectData: { code },
    } = this.state;
    try {
      const reportTime = new Date();
      const blob = await ProjectsApiInstance.GetProjectFacilitatorsExcel(
        projectId,
        this.source
      );
      const dateTime = formatDate(reportTime, DateFormatType.ExcelFilename);
      const fileName = `DreamSaveInsight-ProjectFacilitators-${code}_${dateTime}.xlsx`;
      download(blob, fileName);
      sendEventGA(
        CategoryKeysGA.ProjectsFacilitatorsList,
        ActionKeysGA.DownloadExcel
      );
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status !== HTTP_STATUS.FORBIDDEN) {
          appContext.setErrorToastText(
            intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
          );
        }
      } else {
        appContext.setErrorToastText(
          intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
        );
      }
    }
  };

  /**
   * Handles removing selected facilitators from project
   */
  onRemoveFacilitators = async (): Promise<void> => {
    const { pageData } = this.state;
    const { match, appContext } = this.props;
    const projectId = match?.params.projectId ?? '';
    try {
      this.setStateAttribute({ status: Status.Loading });
      const selectedFacilitators = Object.keys(pageData.selectedRows);
      await ProjectsApiInstance.RemoveProjectFacilitators(
        projectId,
        selectedFacilitators,
        this.source
      );

      this.setStateAttribute({
        status: Status.Success,
      });
      appContext.setSuccessToastText(
        intl.get('LBL_TOAST_PROJECT_FACILITATORS_LIST_REMOVE_SUCCESS')
      );
      this.setEditStateAttribute({
        editStatus: Status.Success,
      });

      sendEventGA(
        CategoryKeysGA.ProjectsFacilitatorsList,
        ActionKeysGA.RemoveFacilitators
      );

      this.toggleEditMode();
      this.toggleRemoveFacilitatorsModal();
    } catch (error) {
      this.setEditStateAttribute({
        editStatus: Status.Error,
      });
      if (error instanceof ApiError) {
        this.setStateAttribute({
          error,
          status: Status.Error,
        });
        if (error.status !== HTTP_STATUS.FORBIDDEN) {
          appContext.setErrorToastText(
            intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
          );
        }
      } else {
        this.setStateAttribute({
          error: new Error(
            intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
          ),
          status: Status.Error,
        });
        appContext.setErrorToastText(
          intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
        );
      }
    } finally {
      if (this.okayButtonRef && this.okayButtonRef.current) {
        this.okayButtonRef.current.blur();
      }
    }
  };

  /**
   * Handles submitting updated facilitators
   */
  onUpdateFacilitators = async (): Promise<void> => {
    const { edit } = this.state;
    const { appContext } = this.props;
    try {
      this.setStateAttribute({ status: Status.Loading });
      const updatedFacilitatorObjects = Object.values(edit.updatedData);
      if (updatedFacilitatorObjects.length > 0) {
        const updatedFacilitators: UpdateFacilitatorsByBulk[] =
          updatedFacilitatorObjects.map(
            ({ id, supervisor }: ProjectFacilitatorViewModel) => ({
              supervisorId: supervisor?.id ?? null,
              id,
            })
          );
        await SettingsApiInstance.UpdateFacilitatorsByBulk(
          updatedFacilitators,
          this.source
        );
      }
      this.setStateAttribute({
        status: Status.Success,
      });
      appContext.setSuccessToastText(
        intl.get('LBL_TOAST_PROJECT_FACILITATORS_LIST_EDIT_SUCCESS')
      );
      sendEventGA(
        CategoryKeysGA.ProjectsFacilitatorsList,
        ActionKeysGA.EditFacilitators
      );

      this.setEditStateAttribute({
        editStatus: Status.Success,
      });
      this.toggleEditMode();
    } catch (error) {
      this.setEditStateAttribute({
        editStatus: Status.Error,
      });
      if (error instanceof ApiError) {
        this.setStateAttribute({
          error,
          status: Status.Error,
        });
        if (error.status !== HTTP_STATUS.FORBIDDEN) {
          appContext.setErrorToastText(
            intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
          );
        }
      } else {
        this.setStateAttribute({
          error: new Error(
            intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
          ),
          status: Status.Error,
        });
        appContext.setErrorToastText(
          intl.get('ERR_TOAST_PROJECT_FACILITATORS_LIST_ERRORS')
        );
      }
    } finally {
      if (this.saveButtonRef && this.saveButtonRef.current) {
        this.saveButtonRef.current.blur();
      }
    }
  };

  /**
   * Renders the export button
   *
   * @returns {JSX.Element} JSX snippet containing the export button component
   */
  renderExportButton = (): JSX.Element => {
    const { exportDisabled, canDownloadCSV, canDownloadPDF } = this.state;
    return (
      <UncontrolledButtonDropdown
        disabled={exportDisabled || (!canDownloadCSV && !canDownloadPDF)}
        className="insight-btn-dropdown"
      >
        <DropdownToggle
          disabled={exportDisabled || (!canDownloadCSV && !canDownloadPDF)}
          tag="button"
          className="btn header-btn"
        >
          <i className="icon-export" />
          {intl.get('LBL_EXPORT')}
        </DropdownToggle>
        <DropdownMenu>
          {canDownloadPDF && (
            <DropdownItem tag="button" className="btn" disabled>
              <i className="icon-pdf" />
              {intl.get('LBL_PDF')}
            </DropdownItem>
          )}
          {canDownloadCSV && (
            <DropdownItem
              tag="button"
              className="btn"
              onClick={this.onExcelDownload}
            >
              <i className="icon-excel" />
              {intl.get('LBL_EXCEL')}
            </DropdownItem>
          )}
        </DropdownMenu>
      </UncontrolledButtonDropdown>
    );
  };

  /**
   * Renders the action buttons to be displayed in the default mode
   *
   * @returns {JSX.Element} JSX snippet containing the buttons component
   */
  renderDefaultActionButtons = (): JSX.Element => {
    const { canEditFacilitators } = this.state;
    return (
      <Col sm="auto" className="btn-col-right">
        {this.renderExportButton()}
        {canEditFacilitators && (
          <Button
            className="btn btn-secondary"
            type="button"
            onClick={this.toggleEditMode}
          >
            <i className="icon-plus" />
            {intl.get('BTN_PROJECT_FACILITATORS_EDIT')}
          </Button>
        )}
      </Col>
    );
  };

  /**
   * Renders the action buttons to be displayed in edit mode
   *
   * @returns {JSX.Element} JSX snippet containing the buttons component
   */
  renderEditActionButtons = (): JSX.Element => {
    const {
      canAddFacilitators,
      data,
      edit: { updatedData },
      pageData: { selectedRows },
    } = this.state;
    const { match } = this.props;
    const projectId = match?.params.projectId ?? '';
    return (
      <Col sm="auto" className="btn-col-right">
        {isEmpty(selectedRows) ? (
          <InsLink
            to={`${ModulePaths.ProjectsPath}/${projectId}${ModulePaths.ProjectAddFacilitatorsPath}`}
            className="btn btn-secondary"
            hide={!canAddFacilitators}
          >
            <i className="icon-plus" />
            {intl.get('BTN_PROJECT_FACILITATORS_ADD_A_FACILITATOR')}
          </InsLink>
        ) : (
          canAddFacilitators && (
            <Button
              className="btn btn-secondary"
              type="button"
              onClick={this.toggleRemoveFacilitatorsModal}
            >
              {intl.get('BTN_PROJECT_FACILITATORS_REMOVE_FACILITATOR')}
            </Button>
          )
        )}
        <button
          className="btn btn-warning"
          type="button"
          onClick={this.toggleEditMode}
        >
          {intl.get('BTN_PROJECT_FACILITATORS_CANCEL')}
        </button>
        <button
          ref={this.saveButtonRef}
          className="btn btn-primary"
          type="button"
          onClick={this.onUpdateFacilitators}
          disabled={isEmpty(data) || isEmpty(updatedData)}
        >
          {intl.get('BTN_PROJECT_FACILITATORS_SAVE')}
        </button>
      </Col>
    );
  };

  /**
   * Renders the remove facilitators modal
   *
   * @returns {JSX.Element} JSX snippet containing the modal component
   */
  renderRemoveFacilitatorsModal = (): JSX.Element => {
    const { status, edit, pageData } = this.state;
    const selectedRows = Object.keys(pageData.selectedRows);
    return (
      <Modal
        size="lg"
        isOpen={edit.showRemoveFacilitatorsModal}
        toggle={this.toggleRemoveFacilitatorsModal}
        backdrop="static"
        centered
        keyboard={false}
        id="removeFacilitatorsTarget"
      >
        <BlockUi tag="div" blocking={status === Status.Loading}>
          <ModalHeader className="increase-font truncate text-center">
            {selectedRows.length > 1
              ? intl.get('LBL_PROJECT_FACILITATORS_REMOVE_MULTIPLE_TITLE')
              : intl.get('LBL_PROJECT_FACILITATORS_REMOVE_SINGLE_TITLE')}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col xs="6" className="btn-col-right">
                <Button
                  className="btn btn-warning"
                  type="button"
                  onClick={this.toggleRemoveFacilitatorsModal}
                >
                  {intl.get('BTN_PROJECT_FACILITATORS_CANCEL')}
                </Button>
              </Col>
              <Col xs="6">
                <button
                  ref={this.okayButtonRef}
                  className="btn btn-primary"
                  type="button"
                  onClick={this.onRemoveFacilitators}
                >
                  {intl.get('BTN_PROJECT_FACILITATORS_OKAY')}
                </button>
              </Col>
            </Row>
          </ModalBody>
        </BlockUi>
      </Modal>
    );
  };

  render(): JSX.Element | null {
    const {
      projectData,
      canAddFacilitators,
      data,
      editModeEnabled,
      pageData,
      edit,
      status,
    } = this.state;
    const { location } = this.props;
    const { page, size, sortBy } = this.getTablePreferencesFromSearch(location);

    return data ? (
      <div className="content-container">
        <Row className="align-items-center">
          <Col>
            {projectData.status === Status.Loading ? (
              <SkeletonTheme color="#fafaf5" highlightColor="#ffffff">
                <Skeleton height={20} />
              </SkeletonTheme>
            ) : (
              <Breadcrumb>
                <BreadcrumbItem>
                  {intl.get('LBL_PROJECT_FACILITATORS_BREADCRUMB_PROJECTS')}
                </BreadcrumbItem>
                <BreadcrumbItem>
                  <EllipsisTooltip
                    tag="span"
                    data-place="top"
                    data-for="insTooltip"
                    data-tip={projectData.name}
                    data-class="overflow-wrap"
                    className="truncate"
                  >
                    {projectData.name}
                  </EllipsisTooltip>
                  <span className="ml-1">{`(#${projectData.code})`}</span>
                </BreadcrumbItem>
                <BreadcrumbItem active>
                  {intl.get('LBL_PROJECT_FACILITATORS_BREADCRUMB_FACILITATORS')}
                </BreadcrumbItem>
              </Breadcrumb>
            )}
          </Col>
          {editModeEnabled
            ? this.renderEditActionButtons()
            : this.renderDefaultActionButtons()}
        </Row>
        <Row>
          <Col>
            <hr className="divider" />
          </Col>
        </Row>
        <div>
          <ScrollToTopOnMount />
          <ProjectFacilitatorsDataTable
            data={data}
            updatedData={edit.updatedData}
            editMode={editModeEnabled}
            canAddFacilitators={canAddFacilitators}
            supervisors={edit.supervisors}
            supervisorsFiltered={edit.supervisorsFiltered}
            supervisorsStatus={edit.supervisorsStatus}
            updateSortBy={this.updateSortBy}
            updatePageSize={this.updatePageSize}
            updatePage={this.updatePage}
            pagination={pageData.pagination}
            controlledPageCount={pageData.pageCount}
            initialPageSize={size}
            initialPageIndex={page}
            initialSortBy={sortBy}
            selectedRows={pageData.selectedRows}
            setSelectedRows={this.setSelectedRows}
            onRowClick={this.handleRowClick}
            onUpdateFacilitator={this.onUpdateFacilitator}
            editStatus={edit.editStatus}
            status={status}
          />
          {this.renderRemoveFacilitatorsModal()}
        </div>
      </div>
    ) : null;
  }
}

export default ProjectFacilitatorsView;
